import { GridLogicOperator } from '@mui/x-data-grid-pro'
import type { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-pro'

/** Free-text search box contents, space separated. */
const SEARCH_PARAM = 'search'
/** How the column filters combine. Absent means And, the grid's own default. */
const LOGIC_PARAM = 'logic'

/** These two need no value, so the URL just says `?email=empty`. */
const VALUELESS_OPERATORS = new Set(['isEmpty', 'isNotEmpty'])

/**
 * Short names for the grid's operators, so the URL reads
 * `?org_name=neq:Personal` instead of `?org_name=doesNotEqual:Personal`.
 *
 * Every operator gets its own short name, even where two of them mean much
 * the same thing. Each column type offers a different set: a date column has
 * `is`, a text column has `equals`. If both became `eq` we would not know
 * which one to turn it back into, and picking the wrong one gives the grid a
 * filter that column cannot show.
 */
const OPERATOR_SHORT_NAMES: Record<string, string> = {
    equals: 'eq',
    doesNotEqual: 'neq',
    contains: 'has',
    doesNotContain: 'nhas',
    startsWith: 'sw',
    endsWith: 'ew',
    isEmpty: 'empty',
    isNotEmpty: 'nempty',
    isAnyOf: 'in',
    after: 'gt',
    onOrAfter: 'gte',
    before: 'lt',
    onOrBefore: 'lte',
    '=': 'eqn',
    '!=': 'neqn',
    '>': 'gtn',
    '>=': 'gten',
    '<': 'ltn',
    '<=': 'lten',
}

const OPERATOR_LONG_NAMES: Record<string, string> = Object.fromEntries(
    Object.entries(OPERATOR_SHORT_NAMES).map(([long, short]) => [short, long])
)

/**
 * Turn a filter value into text for the URL.
 *
 * Dates need help. Dropping one straight into a string gives you
 * "Wed Jul 01 2026 01:00:00 GMT+0100 (British Summer Time)", which is a mess
 * in an address bar and means nothing to whoever you send the link to.
 *
 * When someone picks a date with no time, we write just the day, which is
 * what the date box on screen shows them. A value that does have a time keeps
 * it, so we never quietly round someone's filter to the nearest day.
 */
function serialiseValue(value: unknown): string {
    if (value instanceof Date) {
        const iso = value.toISOString()
        return iso.endsWith('T00:00:00.000Z') ? iso.slice(0, 10) : iso
    }
    return String(value)
}

function shortenOperator(operator: string): string {
    return OPERATOR_SHORT_NAMES[operator] ?? operator
}

/**
 * Names we do not recognise are left alone, so someone typing
 * `?email=contains:bob` by hand still gets what they expect.
 */
function expandOperator(operator: string): string {
    return OPERATOR_LONG_NAMES[operator] ?? operator
}

/**
 * What the URL asks for, on top of the defaults the server sends.
 *
 * A column set to `null` means the URL turned a default off: `?type=` with
 * nothing after it. That is not the same as the column being missing, which
 * means "leave the default alone". Without both we could not tell the two
 * apart, and a default anyone switched off would come back on reload.
 */
export type FilterOverrides = {
    items: Record<string, GridFilterItem | null>
    quickFilterValues?: string[]
    logicOperator?: 'and' | 'or'
}

/** The grid uses these two names itself, so a column called either is hidden. */
const RESERVED_PARAMS = new Set([SEARCH_PARAM, LOGIC_PARAM])

function valuesMatch(a: GridFilterItem, b: GridFilterItem): boolean {
    return (
        a.operator === b.operator &&
        JSON.stringify(a.value ?? null) === JSON.stringify(b.value ?? null)
    )
}

/**
 * Read the filters out of the URL.
 *
 * One column per part, written as `?field=operator:value`, which keeps the
 * URL short enough to read and edit by hand. Only the first colon splits it,
 * so values are free to contain their own, which timestamps do.
 */
export function parseFilterOverrides(
    search: string = window.location.search
): FilterOverrides {
    const params = new URLSearchParams(search)
    const overrides: FilterOverrides = { items: {} }

    const searchTerms = params.get(SEARCH_PARAM)?.trim()
    if (searchTerms) {
        overrides.quickFilterValues = searchTerms.split(/\s+/)
    }

    if (params.get(LOGIC_PARAM) === 'or') {
        overrides.logicOperator = 'or'
    }

    for (const [field, raw] of params.entries()) {
        if (!field || RESERVED_PARAMS.has(field)) {
            continue
        }

        // `?field=` with nothing after it means the default was turned off.
        if (raw === '') {
            overrides.items[field] = null
            continue
        }

        const separator = raw.indexOf(':')
        const operator = expandOperator(
            separator === -1 ? raw : raw.slice(0, separator)
        )
        if (!operator) {
            continue
        }

        if (VALUELESS_OPERATORS.has(operator)) {
            overrides.items[field] = { field, operator }
            continue
        }

        // Someone half way through typing a filter. Not a filter yet, and the
        // server ignores it too.
        if (separator === -1) {
            continue
        }

        overrides.items[field] = {
            field,
            operator,
            value: raw.slice(separator + 1),
        }
    }

    return overrides
}

/** What the grid should show: the server's defaults, with the URL on top. */
export function applyOverrides(
    defaultModel: GridFilterModel,
    overrides: FilterOverrides
): GridFilterModel {
    const items: GridFilterItem[] = []

    for (const item of defaultModel.items) {
        const override = overrides.items[item.field]
        if (override === null) {
            continue // Explicitly cleared.
        }
        items.push(override ?? item)
    }

    // Columns the URL asks for that the defaults say nothing about.
    const defaultFields = new Set(defaultModel.items.map((item) => item.field))
    for (const [field, override] of Object.entries(overrides.items)) {
        if (override && !defaultFields.has(field)) {
            items.push(override)
        }
    }

    /*
     * Always say which, never leave it out. The grid treats a missing value as
     * And, but the server cannot tell "they did not say" from "they chose And",
     * and it used to guess Or, which quietly returned everything.
     */
    const model: GridFilterModel = {
        items,
        logicOperator:
            overrides.logicOperator === 'or'
                ? GridLogicOperator.Or
                : GridLogicOperator.And,
    }
    if (overrides.quickFilterValues?.length) {
        model.quickFilterValues = overrides.quickFilterValues
    }
    return model
}

/**
 * Put the grid's filters into the URL, writing down only what is different
 * from the server's defaults.
 *
 * This replaces the current URL rather than adding to history. Tweaking a
 * filter is not really going somewhere new, and adding an entry per keystroke
 * would make the Back button walk through every half-typed search. Anything
 * else already in the URL is left where it is.
 */
export function writeFilterModelToUrl(
    model: GridFilterModel,
    defaultModel: GridFilterModel
): void {
    const params = new URLSearchParams(window.location.search)
    const defaultsByField = new Map(
        defaultModel.items.map((item) => [item.field, item])
    )

    // Clear out the ones this grid owns. They get written again below.
    for (const field of Array.from(params.keys())) {
        if (RESERVED_PARAMS.has(field) || defaultsByField.has(field)) {
            params.delete(field)
        }
    }
    for (const item of model.items) {
        params.delete(item.field)
    }

    const searchTerms = model.quickFilterValues?.join(' ').trim()
    if (searchTerms) {
        params.set(SEARCH_PARAM, searchTerms)
    } else {
        params.delete(SEARCH_PARAM)
    }

    // And is the default, so the URL only needs to mention Or.
    if (model.logicOperator === 'or') {
        params.set(LOGIC_PARAM, 'or')
    } else {
        params.delete(LOGIC_PARAM)
    }

    const present = new Set<string>()

    for (const item of model.items) {
        present.add(item.field)

        const isValueless = VALUELESS_OPERATORS.has(item.operator)
        const hasValue =
            item.value !== undefined && item.value !== null && item.value !== ''

        // Still being typed, so there is nothing to write down yet.
        if (!isValueless && !hasValue) {
            continue
        }

        const fallback = defaultsByField.get(item.field)
        if (fallback && valuesMatch(item, fallback)) {
            continue // Same as the default, so the URL need not say it.
        }

        const operator = shortenOperator(item.operator)
        params.set(
            item.field,
            isValueless ? operator : `${operator}:${serialiseValue(item.value)}`
        )
    }

    // If someone switched a default off, say so. Otherwise reloading the page
    // would hand it straight back to them.
    for (const field of defaultsByField.keys()) {
        if (!present.has(field)) {
            params.set(field, '')
        }
    }

    /*
     * Two tidy-ups on the finished text.
     *
     * Spaces come out as plus signs by default, which look like part of the
     * value: an organisation called British Summer Time reads as
     * British+Summer+Time. %20 means the same space to everything that reads
     * a URL, and browsers show it as a space. A real plus in someone's value
     * is already written as %2B by this point, so it is safe.
     *
     * Colons are allowed in a URL as they are, and this whole format is built
     * around them, so they go back to being colons.
     */
    const query = params.toString().replace(/%3A/g, ':').replace(/\+/g, '%20')
    window.history.replaceState(
        null,
        '',
        query
            ? `${window.location.pathname}?${query}`
            : window.location.pathname
    )
}
